import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_ENDPOINTS } from "../lib/config";
import { fetchWithAuth } from "../lib/authService";

export default function EditProfilePopup({ onClose, profile, onSuccess }) {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    gender: "",
    dob: { day: "", month: "", year: "" },
    display_name: "",
    location: "",
    instagram: "",
    twitter: "",
    youtube: "",
    linkedIn: "",
    custom: "",
    role: "DJ",
  });
  const roles = ["DJ", "KJ", "Singer", "Live Band"];
  const [profileImage, setProfileImage] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (profile) {
      const [firstName = "", lastName = ""] =
        profile.first_name && profile.last_name
          ? [profile.first_name, profile.last_name]
          : (profile.display_name || "").split(" ");

      // Parse DOB
      let dobParts = { day: "", month: "", year: "" };
      if (profile.dob) {
        const date = new Date(profile.dob);
        dobParts = {
          day: date.getDate().toString(),
          month: (date.getMonth() + 1).toString(),
          year: date.getFullYear().toString(),
        };
      }

      // Get role excluding Admin
      let selectedRole = "DJ"; // default
      if (Array.isArray(profile.role)) {
        const nonAdminRoles = profile.role.filter((r) => r !== "Admin");
        if (nonAdminRoles.length > 0) {
          selectedRole = nonAdminRoles[0]; // pick first non-admin role
        }
      } else if (profile.role && profile.role !== "Admin") {
        selectedRole = profile.role;
      }

      // Strip role prefix from display_name
      const cleanDisplayName =
        profile.display_name
          ?.replace(/^(Singer|DJ|KJ|Live Band)\s*/i, "")
          .trim() || `${firstName}_${lastName}`;

      setFormData({
        first_name: firstName,
        last_name: lastName,
        email: profile.email || "",
        phone_number: profile.phone_number || "",
        gender: profile.gender || "",
        dob: dobParts,
        display_name: cleanDisplayName,
        location: profile.location || "",
        instagram: profile.instagram || "",
        twitter: profile.twitter || "",
        youtube: profile.youtube || "",
        linkedIn: profile.linkedIn || "",
        custom: profile.custom || "",
        role: selectedRole,
      });

      setAvatarUrl(profile.avatar || "");
    }
  }, [profile]);

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setProfileImage(URL.createObjectURL(file));
    const formDataImg = new FormData();
    formDataImg.append("avatar", file);
    const token = localStorage.getItem("tringAuthToken");
    try {
      const response = await fetch(API_ENDPOINTS.PARTNER_UPLOAD_AVATAR, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formDataImg,
      });

      if (!response.ok) throw new Error("Failed to upload avatar");
      const data = await response.json();
      setAvatarUrl(data.avatarUrl);
    } catch (error) {
      console.error("Image upload error:", error);
      toast.error("Failed to upload image");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!localStorage.getItem("tringAuthToken")) {
      navigate("/");
      return;
    }

    // Basic field validations
    if (!formData.first_name.trim()) {
      toast.error("First name cannot be empty");
      return;
    }
    if (!formData.last_name.trim()) {
      toast.error("Last name cannot be empty");
      return;
    }
    if (!formData.email.trim()) {
      toast.error("Email cannot be empty");
      return;
    }
    if (!formData.phone_number.trim()) {
      toast.error("Phone number cannot be empty");
      return;
    }

    const { day, month, year } = formData.dob;
    if (!day || !month || !year) {
      toast.error("Please fill complete date of birth");
      return;
    }

    // Format date as YYYY-MM-DD
    const dobString = `${year}-${month.padStart(2, "0")}-${day.padStart(
      2,
      "0"
    )}`;

    // Construct payload using the correct formData fields
    const payload = {
      first_name: formData.first_name.trim(),
      last_name: formData.last_name.trim(),
      email: formData.email.trim(),
      phone_number: formData.phone_number.trim(),
      gender: formData.gender,
      display_name:
        formData.role +
        " " +
        (formData.display_name ||
          `${formData.first_name}_${formData.last_name}`),
      dob: dobString,
      avatar_url: avatarUrl,
      location: formData.location.trim(),
      instagram: formData.instagram.trim() || "",
      twitter: formData.twitter.trim() || "",
      youtube: formData.youtube.trim() || "",
      linkedIn: formData.linkedIn.trim() || "",
      custom: formData.custom.trim() || "",
      role: formData.role,
    };

    try {
      const res = await fetchWithAuth(API_ENDPOINTS.PARTNER, {
        method: "PUT",
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to update profile");
      }

      const responseBody = await res.json();
      console.log("Profile updated:", responseBody);
      toast.success("Profile updated successfully!");
      setTimeout(() => {
        if (typeof onSuccess === "function") onSuccess();
      }, 800);
    } catch (err) {
      console.error("Update failed:", err);
      toast.error(err.message || "Failed to update profile");
    }
  };

  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const years = Array.from(
    { length: 100 },
    (_, i) => new Date().getFullYear() - i
  );

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center md:p-4 p-2"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <ToastContainer position="top-center" autoClose={3000} />
      <div
        className="bg-white/95 h-max backdrop-blur-lg rounded-2xl shadow-2xl overflow-hidden w-full max-w-2xl h-full flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-2 border-gray-200">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#651FA8] to-[#8A325D]">
              Edit Profile
            </h1>
            <button
              onClick={onClose}
              className="text-2xl text-[#651FA8] hover:text-[#8A325D]"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content - Now with flex-grow and no scroll */}
        <div className="flex-grow px-6 ">
          <form
            onSubmit={handleSubmit}
            className="h-full grid grid-rows-1 gap-2"
          >
            {/* Profile Image */}
            <div className="flex justify-center">
              <div className="relative">
                <div className="h-24 w-24 rounded-full border-2 border-[#651FA8] overflow-hidden bg-gray-100">
                  {profileImage || avatarUrl ? (
                    <img
                      src={profileImage || avatarUrl}
                      alt="Profile"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-gray-400">
                      Photo
                    </div>
                  )}
                </div>
                <label
                  htmlFor="photo-upload"
                  className="absolute bottom-0 right-0 bg-white p-2 rounded-full shadow-md cursor-pointer"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-[#651FA8]"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15.232 5.232l3.536 3.536M4 13.5V21h7.5L21 11.5l-7.5-7.5L4 13.5z"
                    />
                  </svg>
                </label>
                <input
                  id="photo-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageChange}
                />
              </div>
            </div>
            {/* Form Fields Container */}
            <div className="grid grid-cols-1 md:grid-cols-2 md:gap-4 gap-1 auto-rows-min">
              {/* Name Field */}
              <div>
                <label className="block text-xs md:text-sm font-semibold text-[#651FA8] mb-1 md:mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      first_name: e.target.value,
                    }))
                  }
                  className="w-full px-2 py-1 md:px-4 md:py-2 rounded-lg border-2 border-[#651FA8]/30 
                 focus:ring-2 focus:ring-[#651FA8] outline-none text-sm md:text-base"
                  required
                />
              </div>

              {/* Display Name */}
              <div>
                <label className="block text-xs md:text-sm font-semibold text-[#651FA8] mb-1 md:mb-2">
                  Display Name
                </label>
                <input
                  type="text"
                  value={formData.display_name}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      display_name: e.target.value,
                    }))
                  }
                  className="w-full px-2 py-1 md:px-4 md:py-2 rounded-lg border-2 border-[#651FA8]/30 
                 focus:ring-2 focus:ring-[#651FA8] outline-none text-sm md:text-base"
                  required
                />
                <p className="mt-1 mb-0 text-gray-500 text-xs">
                  Visible as {formData.role} {formData.display_name}
                </p>
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs md:text-sm font-semibold text-[#651FA8] mb-1 md:mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, email: e.target.value }))
                  }
                  className="w-full px-2 py-1 md:px-4 md:py-2 rounded-lg border-2 border-[#651FA8]/30 
                 focus:ring-2 focus:ring-[#651FA8] outline-none text-sm md:text-base"
                  required
                />
              </div>

              {/* Role */}
              <div>
                <label className="block text-xs md:text-sm font-semibold text-[#651FA8] mb-1 md:mb-2">
                  Role
                </label>
                <select
                  value={formData.role}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, role: e.target.value }))
                  }
                  className="w-full px-2 py-1 md:px-4 md:py-2 rounded-lg border-2 border-[#651FA8]/30 
                 focus:ring-2 focus:ring-[#651FA8] outline-none text-sm md:text-base"
                >
                  {roles
                    .filter((role) => role !== "Admin")
                    .map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                </select>
              </div>

              {/* Date of Birth - Full width on desktop (2 cols) */}
              <div className="md:col-span-2 mb-2">
                <label className="block text-xs md:text-sm font-semibold text-[#651FA8] mb-1 md:mb-2">
                  Date of Birth
                </label>
                <div className="grid grid-cols-3 gap-1">
                  <select
                    value={formData.dob.day}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        dob: { ...prev.dob, day: e.target.value },
                      }))
                    }
                    className="px-2 py-1 md:px-4 md:py-2 rounded-lg border-2 border-[#651FA8]/30 
                   focus:ring-2 focus:ring-[#651FA8] outline-none text-sm md:text-base"
                    required
                  >
                    <option value="">Day</option>
                    {days.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                  <select
                    value={formData.dob.month}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        dob: { ...prev.dob, month: e.target.value },
                      }))
                    }
                    className="px-2 py-1 md:px-4 md:py-2 rounded-lg border-2 border-[#651FA8]/30 
                   focus:ring-2 focus:ring-[#651FA8] outline-none text-sm md:text-base"
                    required
                  >
                    <option value="">Month</option>
                    {months.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                  <select
                    value={formData.dob.year}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        dob: { ...prev.dob, year: e.target.value },
                      }))
                    }
                    className="px-2 py-1 md:px-4 md:py-2 rounded-lg border-2 border-[#651FA8]/30 
                   focus:ring-2 focus:ring-[#651FA8] outline-none text-sm md:text-base"
                    required
                  >
                    <option value="">Year</option>
                    {years.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-gray-200 bg-white mt-1">
          <div className="flex justify-center items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="cursor-pointer w-32 md:w-40 px-4 py-2 border-2 border-[#651FA8] text-[#651FA8] rounded-lg hover:bg-[#651FA8]/10 transition-colors text-sm md:text-base active:scale-95"
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              className="cursor-pointer w-32 md:w-40 px-4 py-2 bg-[#651FA8] text-white rounded-lg hover:bg-[#5a1697] transition-colors text-sm md:text-base active:scale-95"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
